import React, { useEffect, useMemo, useState } from 'react';
import MyButton from '../../../components/MyButton';
import SearchInput from '../../../components/SearchInput';
import MyLoading from '../../../components/MyLoading';
import {
  CITY_MODES,
  SELECTION_MODES,
  fetchCitiesOfCountryCached,
  getConfigIssues,
  isValidOwnerId,
} from '../../../helpers/organizationsBulkAutofill';
import TemplatePhotosPicker from './TemplatePhotosPicker';

const PHOTOS_PER_OFFER_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function Segment({ options, value, onChange }) {
  return (
    <div className="bulk_offers_segment">
      {options.map((option) => (
        <div
          key={String(option.value)}
          className={`bulk_offers_segment_item ${value === option.value ? 'active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
}

function SetupBlock({ title, hint, children }) {
  return (
    <div className="bulk_offers_setup_block">
      <p className="bulk_offers_setup_block_title">{title}</p>
      {hint ? <p className="bulk_offers_field_note">{hint}</p> : null}
      {children}
    </div>
  );
}

function IntroPanel() {
  return (
    <div className="bulk_offers_panel bulk_offers_setup_intro">
      <p className="bulk_offers_panel_title">Как это работает</p>
      <ol className="bulk_offers_steps">
        <li>
          Выберите режим: что будет одинаковым у всех новых предложений — страна, город,
          категория, подкатегория, владелец, шаблонные фото.
        </li>
        <li>
          Скачайте файл для ИИ: в нём инструкция, выбранный режим и нужные справочники
          (города выбранной страны, категории с подкатегориями, языки).
        </li>
        <li>
          Отправьте файл модели одним сообщением, затем присылайте ей данные о предложениях —
          текстами, выгрузками, ссылками, сколько нужно.
        </li>
        <li>
          Напишите модели «Собери файл» — она вернёт один JSON с массивом
          {' '}
          <code>offers</code>
          .
        </li>
        <li>
          Загрузите этот ответ в блоке справа — появятся карточки предложений: правьте поля
          по одной карточке или сразу у выбранных и создавайте их пачкой.
        </li>
      </ol>
      <p className="bulk_offers_notice">
        Всё, что зафиксировано в режиме, применяется ко всем предложениям принудительно —
        независимо от того, что модель нашла в тексте. В карточках такие поля закрыты для правки.
      </p>
    </div>
  );
}

/**
 * Шаг 1: режим работы (что одинаково у всех предложений) и скачивание файла для ИИ.
 */
function BulkSetupPanel({
  config,
  setConfig,
  categories,
  countries,
  staticLoading,
  locked,
  onDownload,
  downloadLoading,
  downloadError,
}) {
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const isSingleCountry = config.countryMode === SELECTION_MODES.single;
  const isSingleCategory = config.categoryMode === SELECTION_MODES.single;
  const isOnlineCategory = Boolean(isSingleCategory && config.category?.is_online);

  const offlineCategories = useMemo(() => categories.filter((item) => !item.is_online), [categories]);
  const onlineCategories = useMemo(() => categories.filter((item) => item.is_online), [categories]);
  const issues = useMemo(() => getConfigIssues(config), [config]);

  useEffect(() => {
    let cancelled = false;
    const countryId = config.country?._id;
    if (!countryId) {
      setCities([]);
      return undefined;
    }
    setCitiesLoading(true);
    fetchCitiesOfCountryCached(countryId)
      .then((list) => {
        if (!cancelled) setCities(list);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setCitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [config.country?._id]);

  const patchConfig = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  const onCountryModeChange = (countryMode) => patchConfig({
    countryMode,
    country: countryMode === SELECTION_MODES.single ? config.country : null,
    city: null,
    cityMode: CITY_MODES.perOffer,
  });

  const onCountryChange = (countryId) => patchConfig({
    country: countries.find((item) => item._id === countryId) || null,
    city: null,
    cityMode: CITY_MODES.perOffer,
  });

  const onCategoryModeChange = (categoryMode) => patchConfig({
    categoryMode,
    category: categoryMode === SELECTION_MODES.single ? config.category : null,
    subCategory: null,
    subCategoryMode: SELECTION_MODES.all,
    templatePhotoIds: [],
  });

  const onCategoryChange = (categoryId) => {
    const category = categories.find((item) => item._id === categoryId) || null;
    patchConfig({
      category,
      subCategory: null,
      subCategoryMode: SELECTION_MODES.all,
      templatePhotoIds: [],
      ...(category?.is_online ? { city: null, cityMode: CITY_MODES.perOffer } : {}),
    });
  };

  const ownerIdError = config.ownerId && !isValidOwnerId(config.ownerId) ? 'ID владельца — 24 символа' : '';

  if (staticLoading) {
    return (
      <div className="bulk_offers_panel">
        <MyLoading />
      </div>
    );
  }

  return (
    <div className="bulk_offers_setup">
      <IntroPanel />

      <div className="bulk_offers_panel">
        <p className="bulk_offers_panel_title">Режим работы</p>

        {locked ? (
          <p className="bulk_offers_notice">
            Режим закреплён: по нему уже разобраны карточки. Чтобы поменять его, очистите список карточек.
          </p>
        ) : null}

        <fieldset className="bulk_offers_setup_body" disabled={locked}>
          <SetupBlock title="Страна">
            <Segment
              options={[
                { value: SELECTION_MODES.all, label: 'Разные страны' },
                { value: SELECTION_MODES.single, label: 'Одна страна для всех' },
              ]}
              value={config.countryMode}
              onChange={onCountryModeChange}
            />
            {isSingleCountry ? (
              <select
                className="bulk_offers_select"
                value={config.country?._id || ''}
                onChange={(e) => onCountryChange(e.target.value)}
              >
                <option value="">— Выберите страну —</option>
                {countries.map((item) => (
                  <option key={item._id} value={item._id}>{item.name}</option>
                ))}
              </select>
            ) : (
              <p className="bulk_offers_field_note">
                Города в файл не попадут — их придётся выбрать в карточке каждого предложения вручную.
              </p>
            )}
          </SetupBlock>

          {isSingleCountry && config.country && !isOnlineCategory ? (
            <SetupBlock title="Город">
              <Segment
                options={[
                  { value: CITY_MODES.perOffer, label: 'У каждого свой' },
                  { value: CITY_MODES.single, label: 'Один для всех' },
                  { value: CITY_MODES.allCitiesOfCountry, label: 'Все города страны' },
                ]}
                value={config.cityMode}
                onChange={(cityMode) => patchConfig({
                  cityMode,
                  city: cityMode === CITY_MODES.single ? config.city : null,
                })}
              />
              {config.cityMode === CITY_MODES.single ? (
                <select
                  className="bulk_offers_select"
                  value={config.city?._id || ''}
                  onChange={(e) => patchConfig({ city: cities.find((item) => item._id === e.target.value) || null })}
                  disabled={citiesLoading}
                >
                  <option value="">{citiesLoading ? 'Загрузка городов…' : '— Выберите город —'}</option>
                  {cities.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </select>
              ) : null}
              {config.cityMode === CITY_MODES.perOffer ? (
                <p className="bulk_offers_field_note">
                  {`Модель выберет город каждому предложению из списка городов страны ${config.country.name}.`}
                </p>
              ) : null}
              {config.cityMode === CITY_MODES.allCitiesOfCountry ? (
                <p className="bulk_offers_field_note">
                  Предложения будут работать во всех городах страны: адрес и координаты не нужны.
                </p>
              ) : null}
            </SetupBlock>
          ) : null}

          <SetupBlock title="Категория">
            <Segment
              options={[
                { value: SELECTION_MODES.all, label: 'Разные категории' },
                { value: SELECTION_MODES.single, label: 'Одна для всех' },
              ]}
              value={config.categoryMode}
              onChange={onCategoryModeChange}
            />
            {isSingleCategory ? (
              <select
                className="bulk_offers_select"
                value={config.category?._id || ''}
                onChange={(e) => onCategoryChange(e.target.value)}
              >
                <option value="">— Выберите категорию —</option>
                <optgroup label="Обычные категории">
                  {offlineCategories.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Онлайн категории">
                  {onlineCategories.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </optgroup>
              </select>
            ) : (
              <p className="bulk_offers_field_note">
                Модель подберёт категорию и подкатегории каждому предложению сама, с учётом онлайн/офлайн.
              </p>
            )}
            {isOnlineCategory ? (
              <p className="bulk_offers_field_note">
                Категория онлайн: город, адрес и координаты у таких предложений не заполняются.
              </p>
            ) : null}
          </SetupBlock>

          {isSingleCategory && config.category?.sub_categories?.length ? (
            <SetupBlock title="Подкатегория">
              <Segment
                options={[
                  { value: SELECTION_MODES.all, label: 'Разные подкатегории' },
                  { value: SELECTION_MODES.single, label: 'Одна для всех' },
                ]}
                value={config.subCategoryMode}
                onChange={(subCategoryMode) => patchConfig({
                  subCategoryMode,
                  subCategory: subCategoryMode === SELECTION_MODES.single ? config.subCategory : null,
                  templatePhotoIds: [],
                })}
              />
              {config.subCategoryMode === SELECTION_MODES.single ? (
                <select
                  className="bulk_offers_select"
                  value={config.subCategory?._id || ''}
                  onChange={(e) => patchConfig({
                    subCategory: config.category.sub_categories.find((item) => item._id === e.target.value) || null,
                    templatePhotoIds: [],
                  })}
                >
                  <option value="">— Выберите подкатегорию —</option>
                  {config.category.sub_categories.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </select>
              ) : null}
            </SetupBlock>
          ) : null}

          <SetupBlock
            title="ID владельца"
            hint="Один владелец на все создаваемые предложения — 24 символа."
          >
            <SearchInput
              wrapperClassName="bulk_offers_input"
              withLeftIcon={false}
              placeholder="ID владельца"
              value={config.ownerId}
              onChange={(e) => patchConfig({ ownerId: typeof e === 'string' ? e : e.target.value })}
              shouldValueConsiderAsFocus={false}
              error={ownerIdError}
            />
          </SetupBlock>

          {isSingleCategory && config.category ? (
            <SetupBlock
              title="Шаблонные фото"
              hint="Выбранные фото раздаются случайно каждому предложению. Без хотя бы одного фото предложение создать нельзя."
            >
              <TemplatePhotosPicker
                value={config.templatePhotoIds}
                onChange={(templatePhotoIds) => patchConfig({
                  templatePhotoIds,
                  photosPerOffer: Math.min(config.photosPerOffer, Math.max(templatePhotoIds.length, 1)),
                })}
                categories={categories}
                lockedCategoryId={config.category._id}
                lockedSubCategoryId={config.subCategory?._id || ''}
                initialCategoryId={config.category._id}
                initialSubCategoryId={config.subCategory?._id || ''}
              />
              <div className="bulk_offers_field_inline bulk_offers_field_inline_center">
                <p className="bulk_offers_field_note">Фото на одно предложение</p>
                <select
                  className="bulk_offers_select bulk_offers_select_narrow"
                  value={config.photosPerOffer}
                  onChange={(e) => patchConfig({ photosPerOffer: Number(e.target.value) })}
                >
                  {PHOTOS_PER_OFFER_OPTIONS.map((count) => (
                    <option
                      key={count}
                      value={count}
                      disabled={count > Math.max(config.templatePhotoIds.length, 1)}
                    >
                      {count}
                    </option>
                  ))}
                </select>
              </div>
            </SetupBlock>
          ) : (
            <SetupBlock
              title="Шаблонные фото"
              hint="Шаблонные фото лежат в папках категорий, поэтому заранее выбрать их можно только при одной категории для всех. При разных категориях выберите фото уже в карточках — по одной или сразу у выбранных."
            />
          )}
        </fieldset>

        <MyButton
          text="Скачать файл для ИИ"
          onClick={onDownload}
          loading={downloadLoading}
          disabled={Boolean(issues.length)}
          className="bulk_offers_wide_button"
        />
        {issues.length ? (
          <ul className="bulk_offers_issues">
            {issues.map((issue) => <li key={issue}>{issue}</li>)}
          </ul>
        ) : null}
        {downloadError ? <p className="bulk_offers_error">{downloadError}</p> : null}
      </div>
    </div>
  );
}

export default BulkSetupPanel;
